import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { 
  ArrowLeft, Save, Upload, Download, Trash2, AlertTriangle, 
  CheckCircle, CalendarDays, Eye, Mail, Home
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import EmailHistory from './EmailHistory';

const DOCUMENT_TYPES = [
  { key: 'driving_license_front', label: 'Driving License (Front)' },
  { key: 'driving_license_back', label: 'Driving License (Back)' },
  { key: 'proof_of_address_1', label: 'Proof of Address 1' },
  { key: 'proof_of_address_2', label: 'Proof of Address 2' },
  { key: 'dvla_check_code_result', label: 'DVLA Check Code Result', expiryDays: 21 },
];

const CustomerForm = () => {
  const navigate = useNavigate();
  const { customerId } = useParams();
  const isEditMode = !!customerId;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [displayId, setDisplayId] = useState('');
  const [customer, setCustomer] = useState({
    first_name: '',
    surname: '',
    address: '',
    postcode: '',
    telephone: '',
    email: '',
    dvla_check_code: '',
    bank_account_number: '',
    sort_code: '',
    account_holder_name: '',
    additional_notes: '',
  });
  const [documents, setDocuments] = useState({});
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [editingCheckedDate, setEditingCheckedDate] = useState(null);
  const [deleteDocType, setDeleteDocType] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchCustomer = useCallback(async () => {
    if (!customerId) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/customers/${customerId}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setCustomer({
          first_name: data.first_name || '',
          surname: data.surname || '',
          address: data.address || '',
          postcode: data.postcode || '',
          telephone: data.telephone || '',
          email: data.email || '',
          dvla_check_code: data.dvla_check_code || '',
          bank_account_number: data.bank_account_number || '',
          sort_code: data.sort_code || '',
          account_holder_name: data.account_holder_name || '',
          additional_notes: data.additional_notes || '',
        });
        setDisplayId(data.display_id || '');
        setDocuments(data.documents || {});
      } else {
        toast.error('Customer not found');
        navigate('/admin/customers');
      }
    } catch (error) {
      console.error('Error fetching customer:', error);
      toast.error('Failed to load customer');
    } finally {
      setLoading(false);
    }
  }, [API_URL, customerId, navigate]);

  useEffect(() => {
    if (isEditMode) {
      fetchCustomer();
    }
  }, [isEditMode, fetchCustomer]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCustomer(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!customer.first_name || !customer.surname) {
      toast.error('First name and surname are required');
      return;
    }

    setSaving(true);
    try {
      const url = isEditMode 
        ? `${API_URL}/api/customers/${customerId}`
        : `${API_URL}/api/customers`;
      
      const response = await fetch(url, {
        method: isEditMode ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(customer)
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(isEditMode ? 'Customer updated' : 'Customer created');
        
        if (!isEditMode) {
          navigate(`/admin/customers/${data.customer_id}`);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save customer');
      }
    } catch (error) {
      console.error('Error saving customer:', error);
      toast.error('Failed to save customer');
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (docType, file) => {
    if (!customerId) {
      toast.error('Please save the customer first before uploading documents');
      return;
    }

    setUploadingDoc(docType);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(
        `${API_URL}/api/customers/${customerId}/documents/${docType}`,
        {
          method: 'POST',
          credentials: 'include',
          body: formData
        }
      );

      if (response.ok) {
        const data = await response.json();
        setDocuments(prev => ({
          ...prev,
          [docType]: { ...data.document, is_expired: false }
        }));
        toast.success('Document uploaded');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to upload document');
      }
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error('Failed to upload document');
    } finally {
      setUploadingDoc(null);
    }
  };

  const handleDownloadDocument = async (docType) => {
    try {
      const response = await fetch(
        `${API_URL}/api/customers/${customerId}/documents/${docType}/file`,
        { credentials: 'include' }
      );

      if (response.ok) {
        const blob = await response.blob();
        const doc = documents[docType];
        const filename = doc?.original_name || `${docType}.pdf`;
        
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else {
        toast.error('Failed to download document');
      }
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error('Failed to download document');
    }
  };

  const handleUpdateCheckedDate = async (docType, newDate) => {
    try {
      const formData = new FormData();
      formData.append('checked_date', newDate);

      const response = await fetch(
        `${API_URL}/api/customers/${customerId}/documents/${docType}/checked-date`,
        {
          method: 'PUT',
          credentials: 'include',
          body: formData
        }
      );

      if (response.ok) {
        // Recalculate if expired
        const checkedDate = new Date(newDate);
        const threeMonthsAgo = new Date();
        threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
        const isExpired = checkedDate < threeMonthsAgo;

        setDocuments(prev => ({
          ...prev,
          [docType]: { ...prev[docType], checked_date: newDate, is_expired: isExpired }
        }));
        toast.success('Check date updated');
      } else {
        toast.error('Failed to update check date');
      }
    } catch (error) {
      console.error('Error updating check date:', error);
      toast.error('Failed to update check date');
    } finally {
      setEditingCheckedDate(null);
    }
  };

  const handleDeleteDocument = async () => {
    if (!deleteDocType) return;

    try {
      const response = await fetch(
        `${API_URL}/api/customers/${customerId}/documents/${deleteDocType}`,
        {
          method: 'DELETE',
          credentials: 'include'
        }
      );

      if (response.ok) {
        setDocuments(prev => {
          const updated = { ...prev };
          delete updated[deleteDocType];
          return updated;
        });
        toast.success('Document deleted');
      } else {
        toast.error('Failed to delete document');
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('Failed to delete document');
    } finally {
      setDeleteDocType(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Not set';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1a1f1a] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#c9a55a]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1a1f1a]" data-testid="customer-form-page">
      {/* Header */}
      <header className="bg-[#2d5f3f] shadow-lg">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/admin')}
                className="text-[#f5f3ed] hover:bg-[#1a1f1a]/20"
                title="Home"
              >
                <Home className="w-5 h-5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/admin/customers')}
                className="text-[#f5f3ed] hover:bg-[#1a1f1a]/20"
                title="Back to Customers"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#f5f3ed]">
                {isEditMode ? 'Edit Customer' : 'New Customer'}
              </h1>
              {displayId && (
                <p className="text-[#c9a55a] text-sm font-mono">{displayId}</p>
              )}
            </div>
          </div>
          {isEditMode && (
            <div className="flex gap-2">
              <Button
                onClick={() => navigate(`/admin/bookings/new?customer=${customerId}`)}
                variant="outline"
                className="border-[#c9a55a] text-[#c9a55a] hover:bg-[#c9a55a]/10"
              >
                <CalendarDays className="w-4 h-4 mr-2" />
                Create Booking
              </Button>
              <Button
                onClick={() => navigate(`/admin/bookings?customer=${customerId}`)}
                variant="outline"
                className="border-[#2d5f3f] text-[#f5f3ed] hover:bg-[#2d5f3f]/50"
              >
                View Bookings
              </Button>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <form onSubmit={handleSubmit}>
          {/* Personal Information */}
          <div className="bg-[#2d5f3f]/30 rounded-xl p-6 border border-[#2d5f3f] mb-6">
            <h2 className="text-lg font-semibold text-[#f5f3ed] mb-4">Personal Information</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-[#f5f3ed]/70">First Name *</Label>
                <Input
                  name="first_name"
                  value={customer.first_name}
                  onChange={handleInputChange}
                  className="bg-[#1a1f1a]/50 border-[#2d5f3f] text-[#f5f3ed]"
                  required
                  data-testid="customer-first-name"
                />
              </div>
              <div>
                <Label className="text-[#f5f3ed]/70">Surname *</Label>
                <Input
                  name="surname"
                  value={customer.surname}
                  onChange={handleInputChange}
                  className="bg-[#1a1f1a]/50 border-[#2d5f3f] text-[#f5f3ed]"
                  required
                  data-testid="customer-surname"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-[#f5f3ed]/70">Address</Label>
                <Textarea
                  name="address"
                  value={customer.address}
                  onChange={handleInputChange}
                  className="bg-[#1a1f1a]/50 border-[#2d5f3f] text-[#f5f3ed]"
                  rows={2}
                  data-testid="customer-address"
                />
              </div>
              <div>
                <Label className="text-[#f5f3ed]/70">Postcode</Label>
                <Input
                  name="postcode"
                  value={customer.postcode}
                  onChange={handleInputChange}
                  className="bg-[#1a1f1a]/50 border-[#2d5f3f] text-[#f5f3ed]"
                  data-testid="customer-postcode"
                />
              </div>
              <div>
                <Label className="text-[#f5f3ed]/70">Telephone</Label>
                <Input
                  name="telephone"
                  value={customer.telephone}
                  onChange={handleInputChange}
                  className="bg-[#1a1f1a]/50 border-[#2d5f3f] text-[#f5f3ed]"
                  data-testid="customer-telephone"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-[#f5f3ed]/70">Email Address</Label>
                <Input
                  name="email"
                  type="email"
                  value={customer.email}
                  onChange={handleInputChange}
                  className="bg-[#1a1f1a]/50 border-[#2d5f3f] text-[#f5f3ed]"
                  data-testid="customer-email"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-[#f5f3ed]/70">DVLA Check Code</Label>
                <Input
                  name="dvla_check_code"
                  value={customer.dvla_check_code}
                  onChange={handleInputChange}
                  className="bg-[#1a1f1a]/50 border-[#2d5f3f] text-[#f5f3ed]"
                  data-testid="customer-dvla-code"
                />
                {customer.dvla_check_code && customer.dvla_code_days_remaining !== undefined && (
                  <div className={`mt-2 flex items-center gap-2 text-sm ${
                    customer.dvla_code_days_remaining <= 3 
                      ? 'text-red-400' 
                      : customer.dvla_code_days_remaining <= 7 
                      ? 'text-orange-400' 
                      : 'text-green-400'
                  }`}>
                    <AlertTriangle className="w-4 h-4" />
                    <span>
                      {customer.dvla_code_expired 
                        ? 'DVLA code has expired and will be cleared on save'
                        : `Valid for ${customer.dvla_code_days_remaining} more day${customer.dvla_code_days_remaining !== 1 ? 's' : ''}`
                      }
                    </span>
                  </div>
                )}
                <p className="text-[#f5f3ed]/40 text-xs mt-1">DVLA codes expire after 21 days</p>
              </div>
            </div>
          </div>

          {/* Bank Details */}
          <div className="bg-[#2d5f3f]/30 rounded-xl p-6 border border-[#2d5f3f] mb-6">
            <h2 className="text-lg font-semibold text-[#f5f3ed] mb-4">Bank Details</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-[#f5f3ed]/70">Account Holder Name</Label>
                <Input
                  name="account_holder_name"
                  value={customer.account_holder_name}
                  onChange={handleInputChange}
                  className="bg-[#1a1f1a]/50 border-[#2d5f3f] text-[#f5f3ed]"
                  data-testid="customer-account-holder"
                />
              </div>
              <div>
                <Label className="text-[#f5f3ed]/70">Sort Code</Label>
                <Input
                  name="sort_code"
                  value={customer.sort_code}
                  onChange={handleInputChange}
                  className="bg-[#1a1f1a]/50 border-[#2d5f3f] text-[#f5f3ed]"
                  placeholder="00-00-00"
                  data-testid="customer-sort-code"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-[#f5f3ed]/70">Bank Account Number</Label>
                <Input
                  name="bank_account_number"
                  value={customer.bank_account_number}
                  onChange={handleInputChange}
                  className="bg-[#1a1f1a]/50 border-[#2d5f3f] text-[#f5f3ed]"
                  data-testid="customer-account-number"
                />
              </div>
            </div>
          </div>

          {/* Additional Notes */}
          <div className="bg-[#2d5f3f]/30 rounded-xl p-6 border border-[#2d5f3f] mb-6">
            <h2 className="text-lg font-semibold text-[#f5f3ed] mb-4">Additional Notes</h2>
            
            <Textarea
              name="additional_notes"
              value={customer.additional_notes}
              onChange={handleInputChange}
              className="bg-[#1a1f1a]/50 border-[#2d5f3f] text-[#f5f3ed]"
              rows={4}
              placeholder="Any additional notes about this customer..."
              data-testid="customer-additional-notes"
            />
          </div>

          {/* Documents Section - Only show in edit mode */}
          {isEditMode && (
            <div className="bg-[#2d5f3f]/30 rounded-xl p-6 border border-[#2d5f3f] mb-6">
              <h2 className="text-lg font-semibold text-[#f5f3ed] mb-4">Documents</h2>
              
              <div className="space-y-4">
                {DOCUMENT_TYPES.map(({ key, label, expiryDays }) => {
                  const doc = documents[key];
                  const hasDoc = doc && (doc.filename || doc.has_file || doc.original_name);
                  const isExpired = doc?.is_expired;
                  const daysRemaining = doc?.days_remaining;
                  const isShortExpiry = expiryDays === 21;

                  return (
                    <div 
                      key={key}
                      className={`p-4 rounded-lg border ${
                        isExpired 
                          ? 'bg-red-500/10 border-red-500/50' 
                          : hasDoc 
                          ? 'bg-green-500/10 border-green-500/50'
                          : 'bg-[#1a1f1a]/50 border-[#2d5f3f]'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {hasDoc ? (
                            isExpired ? (
                              <AlertTriangle className="w-5 h-5 text-red-400" />
                            ) : (
                              <CheckCircle className="w-5 h-5 text-green-400" />
                            )
                          ) : (
                            <div className="w-5 h-5 rounded-full border-2 border-[#f5f3ed]/30" />
                          )}
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="text-[#f5f3ed] font-medium">{label}</p>
                              {isShortExpiry && (
                                <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded">
                                  21-day expiry
                                </span>
                              )}
                            </div>
                            {hasDoc && (
                              <div className="flex items-center gap-2 mt-1">
                                <p className="text-[#f5f3ed]/50 text-sm">
                                  {doc.original_name}
                                </p>
                                {editingCheckedDate === key ? (
                                  <Input
                                    type="date"
                                    defaultValue={doc.checked_date?.split('T')[0]}
                                    className="w-auto h-7 text-xs bg-[#1a1f1a] border-[#2d5f3f] text-[#f5f3ed]"
                                    onBlur={(e) => handleUpdateCheckedDate(key, e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        handleUpdateCheckedDate(key, e.target.value);
                                      }
                                    }}
                                    autoFocus
                                  />
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => setEditingCheckedDate(key)}
                                    className={`text-xs px-2 py-0.5 rounded ${
                                      isExpired 
                                        ? 'bg-red-500/20 text-red-400' 
                                        : 'bg-[#c9a55a]/20 text-[#c9a55a]'
                                    }`}
                                  >
                                    Checked: {formatDate(doc.checked_date)}
                                    {isExpired ? ' (EXPIRED)' : daysRemaining !== undefined && isShortExpiry ? ` (${daysRemaining} days left)` : ''}
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          {hasDoc && (
                            <>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDownloadDocument(key)}
                                className="text-[#c9a55a] hover:bg-[#c9a55a]/10"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => setDeleteDocType(key)}
                                className="text-red-400 hover:bg-red-500/10"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                          <label className="cursor-pointer">
                            <input
                              type="file"
                              className="hidden"
                              onChange={(e) => {
                                if (e.target.files?.[0]) {
                                  handleFileUpload(key, e.target.files[0]);
                                }
                              }}
                            />
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="border-[#c9a55a] text-[#c9a55a] hover:bg-[#c9a55a]/10 pointer-events-none"
                              disabled={uploadingDoc === key}
                            >
                              {uploadingDoc === key ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#c9a55a]" />
                              ) : (
                                <>
                                  <Upload className="w-4 h-4 mr-1" />
                                  {hasDoc ? 'Replace' : 'Upload'}
                                </>
                              )}
                            </Button>
                          </label>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Email History Section - Only show in edit mode */}
          {isEditMode && (
            <div className="bg-[#2d5f3f]/30 rounded-xl p-6 border border-[#2d5f3f] mb-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-[#f5f3ed] flex items-center gap-2">
                  <Mail className="w-5 h-5 text-blue-400" />
                  Email History
                </h2>
                <Button
                  type="button"
                  onClick={() => navigate(`/admin/send-email?customers=${customerId}`)}
                  variant="outline"
                  size="sm"
                  className="border-blue-400 text-blue-400 hover:bg-blue-500/10"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Send Email
                </Button>
              </div>
              <EmailHistory customerId={customerId} />
            </div>
          )}

          {/* Submit Button */}
          <div className="flex justify-end gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/admin/customers')}
              className="border-[#2d5f3f] text-[#f5f3ed] hover:bg-[#2d5f3f]/50"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="bg-[#c9a55a] hover:bg-[#b8944a] text-[#1a1f1a] font-semibold"
              data-testid="save-customer-btn"
            >
              {saving ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#1a1f1a]" />
              ) : (
                <>
                  <Save className="w-5 h-5 mr-2" />
                  {isEditMode ? 'Save Changes' : 'Create Customer'}
                </>
              )}
            </Button>
          </div>
        </form>
      </main>

      {/* Delete Document Dialog */}
      <AlertDialog open={!!deleteDocType} onOpenChange={() => setDeleteDocType(null)}>
        <AlertDialogContent className="bg-[#1a1f1a] border-[#2d5f3f]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#f5f3ed]">Delete Document?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#f5f3ed]/70">
              This will permanently delete this document. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#2d5f3f] text-[#f5f3ed] hover:bg-[#2d5f3f]/50">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteDocument}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CustomerForm;
